'use client';

import { useEffect, useRef, useState } from 'react';
import { BlockRoot, tx } from '@akademi/site-builder-runtime';
import { useSolarText } from '../locales/useSolarText';
import { ActionLink, AddButton, LivePrice, type LinkAction } from '../home/parts';
import { Scene, SCENE_ARTS, type SceneArt } from '../home/sceneArt';
import { totalOf, type LiveMoney } from '../home/money';
import { useAddLines, useLiveLines, type LiveLine, type LiveLines } from '../home/useLiveProducts';
import { TradeStyles } from '../styles/TradeStyles';
import { HOME_CSS } from '../styles/homeCss';

type Localized = string | Record<string, string>;

export interface SolarOfferSlide {
  eyebrow?: Localized;
  title: Localized;
  text?: Localized;
  /** A photograph. Without one the slide draws `art`. */
  image?: { src: string; alt?: Localized };
  art?: SceneArt;
  /**
   * The products this slide sells, read live. One line is a product offer and shows its unit
   * price; several are a kit and show their total. None is a slide that sells an idea (a trade
   * account) and shows no price.
   */
  lines?: LiveLine[];
  /** Words after the price's basis, e.g. "each" or "panels + inverter". */
  priceNote?: Localized;
  facts?: Array<{ value: Localized; label: Localized }>;
  /** Shown on the add-to-basket button. Set it to sell the lines from the slide. */
  addLabel?: Localized;
  primary?: LinkAction;
  secondary?: LinkAction;
}

export interface SolarOfferSliderProps {
  slides?: SolarOfferSlide[];
  /** Seconds each slide stays. 0 turns the rotation off. */
  interval?: number;
  label?: Localized;
}

/**
 * The home page's image slider: one offer at a time, full width, with the text over the picture.
 *
 * <p>Owner's choice, 27 September 2026 ("Home B"): the first screen carries one message, not
 * four boxes. Every price on it is read live from the catalogue (see `useLiveLines`) because a
 * number typed into page data is wrong the first time the feed reprices.
 *
 * <p>It rotates on its own, and stops while the pointer is over it or focus is inside it, and
 * never for a visitor who has asked their system for reduced motion. Every slide is in the
 * server markup, stacked in one grid cell so the slider is as tall as its tallest slide; only
 * the current one is visible, and the others are `inert` so a keyboard does not tab into
 * buttons nobody can see.
 */
export function SolarOfferSlider({ slides = [], interval = 7, label }: SolarOfferSliderProps) {
  const t = useSolarText();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const count = slides.length;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = () => setReduced(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (count < 2 || paused || reduced || !(interval > 0)) return;
    const timer = window.setInterval(() => setCurrent((index) => (index + 1) % count), interval * 1000);
    return () => window.clearInterval(timer);
  }, [count, paused, reduced, interval, current]);

  if (count === 0) return null;
  const go = (index: number) => setCurrent(((index % count) + count) % count);

  return (
    <BlockRoot as="section" className="ae ae-wrap" aria-roledescription="carousel" aria-label={tx(label) || t('home.offers')}>
      <TradeStyles id="home" css={HOME_CSS} />
      <div
        ref={root}
        className="ae-slider"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={(event) => {
          if (!root.current?.contains(event.relatedTarget as Node | null)) setPaused(false);
        }}
      >
        {slides.map((slide, index) => (
          <Slide
            key={index}
            slide={slide}
            on={index === current}
            first={index === 0}
            label={t('home.slideOf', { n: index + 1, total: count })}
          />
        ))}
        {count > 1 ? (
          <>
            <div className="ae-slider__dots">
              {slides.map((slide, index) => (
                <button
                  key={index}
                  type="button"
                  className="ae-slider__dot"
                  aria-label={tx(slide.title) || t('home.slideOf', { n: index + 1, total: count })}
                  aria-current={index === current ? 'true' : undefined}
                  onClick={() => go(index)}
                />
              ))}
            </div>
            <div className="ae-slider__arrows">
              <button type="button" className="ae-slider__arrow" aria-label={t('home.previous')} onClick={() => go(current - 1)}>
                ‹
              </button>
              <button type="button" className="ae-slider__arrow" aria-label={t('home.next')} onClick={() => go(current + 1)}>
                ›
              </button>
            </div>
          </>
        ) : null}
      </div>
    </BlockRoot>
  );
}

function Slide({ slide, on, first, label }: { slide: SolarOfferSlide; on: boolean; first: boolean; label: string }) {
  const live = useLiveLines(slide.lines);
  const adder = useAddLines(slide.lines, live.products);
  const hasLines = !!slide.lines?.length;
  const art: SceneArt = slide.art && SCENE_ARTS.includes(slide.art) ? slide.art : 'roof';
  const outOfStock = live.products.some((product) => product?.availability === 'OUT_OF_STOCK');
  const sellable = hasLines && !!tx(slide.addLabel) && adder.available;
  // The first slide is the page's heading; the rest are not headings of the page at all.
  const Title = first ? 'h1' : 'h2';

  return (
    <div
      className={`ae-slide${on ? ' is-on' : ''}`}
      role="group"
      aria-roledescription="slide"
      aria-label={label}
      aria-hidden={on ? undefined : true}
      inert={on ? undefined : true}
    >
      <div className="ae-slide__media">
        {slide.image?.src ? (
          <img src={slide.image.src} alt={tx(slide.image.alt)} loading={first ? 'eager' : 'lazy'} />
        ) : (
          <Scene art={art} />
        )}
      </div>
      <div className="ae-slide__body">
        {tx(slide.eyebrow) ? <span className="ae-eyebrow">{tx(slide.eyebrow)}</span> : null}
        <Title className="ae-slide__title">{tx(slide.title)}</Title>
        {tx(slide.text) ? <p className="ae-slide__text">{tx(slide.text)}</p> : null}
        {hasLines ? (
          <LivePrice
            size="lg"
            loading={live.loading}
            note={slide.priceNote}
            total={slide.lines!.length === 1 ? unitTotal(live) : live.total}
          />
        ) : null}
        {slide.facts?.length ? (
          <ul className="ae-facts">
            {slide.facts.map((fact, index) => (
              <li key={index}>
                <b>{tx(fact.value)}</b> {tx(fact.label)}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="ae-actions">
          {sellable ? (
            <AddButton
              label={slide.addLabel!}
              state={adder.state}
              disabled={!live.buyable || adder.busy}
              outOfStock={outOfStock}
              onAdd={adder.add}
            />
          ) : (
            <ActionLink action={slide.primary} tone="accent" />
          )}
          {sellable ? <ActionLink action={slide.primary} tone="ghost" /> : null}
          <ActionLink action={slide.secondary} tone="ghost" />
        </div>
      </div>
    </div>
  );
}

/** A one-line offer shows what ONE costs, whatever quantity its button adds. */
function unitTotal(live: LiveLines) {
  const product = live.products[0];
  return product ? totalOf([{ price: product.price as LiveMoney | undefined, quantity: 1 }]) : null;
}
